import { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import produce from "immer";
import SidebarLightbox from "../../refresh/SidebarLightbox";
import styles from "./sharedStyles";
import GenericOptions from "../GenericOptions";
import RedButton from "../../refresh/RedButton";
import Selector from "../../Selector";
import Label from "../../refresh/Label";
import TextInput from "../../refresh/inputs/TextInput";
import { mediaDisplayUrl } from "../../refresh/selectors/uploadedMedia";
import { fetchApi } from "../../../services/api";

const useStyles = createUseStyles({
  ...styles,
  preview: {
    display: "flex",
    "flex-direction": "column",
    "align-items": "center",
    "justify-content": "center",
    padding: "40px 20px",
    "min-height": 200,
    "background-color": "#1a1a1a",
    "border-radius": 8,
    margin: [20, 0],
    position: "relative",
  },
  previewLabel: {
    color: "#9F9F9F",
    "font-size": 12,
    "margin-bottom": 20,
    "text-transform": "uppercase",
  },
  previewContainer: {
    position: "relative",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    width: "100%",
    height: 150,
    overflow: "hidden",
  },
  referenceHotspot: {
    width: 45,
    height: 45,
    "border-radius": 4,
    border: "2px dashed #707070",
    "background-color": "rgba(112, 112, 112, 0.2)",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    color: "#9F9F9F",
    "font-size": 10,
    position: "absolute",
    left: "20%",
  },
  stickerPreview: {
    "border-radius": 4,
    border: "2px solid #17BEDA",
    "background-color": "rgba(23, 190, 218, 0.2)",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    color: "#17BEDA",
    "font-size": 12,
    "font-weight": "bold",
    position: "absolute",
    "transition": "all 0.3s ease",
    overflow: "hidden",
    "box-sizing": "border-box",
  },
  previewIcon: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  previewFallback: {
    "font-size": 10,
    opacity: 0.7,
  },
  sizeInfo: {
    color: "#9F9F9F",
    "font-size": 11,
    "margin-top": 15,
    "text-align": "center",
  },
});

const EditStickerHotspot = ({
  show,
  hotspot,
  account,
  onClose,
  onComplete,
  template = false,
}) => {
  const classes = useStyles();
  const [config, setConfig] = useState(
    produce(hotspot, draft => {
      // Stickers always have tag enabled
      draft.options.tag = true;
      // Initialize sticker size if not set (default to medium)
      draft.options.stickerSize ??= "medium";
      // Initialize custom scale if not set
      draft.options.customScale ??= null;
      // Initialize _options if not present
      draft._options ??= {};
      // Set scale from stickerSize or customScale
      if (draft.options.customScale !== null && draft.options.customScale !== undefined) {
        draft._options.scale = parseFloat(draft.options.customScale) || 2;
      } else {
        const sizeMap = { small: 1, medium: 2, large: 3, xlarge: 4 };
        draft._options.scale = sizeMap[draft.options.stickerSize] ?? 2;
      }
    }),
  );
  const [sizeSelectorOpen, setSizeSelectorOpen] = useState(false);
  const [iconUrl, setIconUrl] = useState(null);

  // Load icon URL when config.icon changes
  useEffect(() => {
    if (!config.icon) {
      setIconUrl(null);
      return;
    }

    const displayUrl = mediaDisplayUrl(config.icon);
    if (displayUrl) {
      setIconUrl(displayUrl);
      return;
    }

    if (config.icon.url) {
      setIconUrl(
        `${window.location.origin}/tour/assets/hotspots/${config.icon.url}`,
      );
      return;
    }

    if (config.icon.id) {
      fetchApi(`/media/${config.icon.id}`, { version: "v2" })
        .then(data => setIconUrl(data.preview_url || data.public_url))
        .catch(e => {
          console.error("Error loading icon:", e);
          setIconUrl(null);
        });
      return;
    }

    setIconUrl(null);
    // Primitive fields only: a new icon object each render must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.icon?.id,
    config.icon?.url,
    config.icon?.preview_url,
    config.icon?.public_url,
  ]);

  // Calculate current scale for preview
  const getCurrentScale = () => {
    if (config.options.customScale !== null && config.options.customScale !== undefined && config.options.customScale !== "") {
      return parseFloat(config.options.customScale) || 2;
    } else {
      const sizeMap = { small: 1, medium: 2, large: 3, xlarge: 4 };
      return sizeMap[config.options.stickerSize ?? "medium"] ?? 2;
    }
  };

  const currentScale = getCurrentScale();
  const stickerSize = 45 * currentScale * 0.8; // Base size is 45px - 0.8 factor manually added

  return (
    <SidebarLightbox
      show={show}
      onClose={onClose}
      sidebar={
        <>
          <h1 className={classes.title}>
            Edit {template ? "Sticker Template" : "Sticker"}
          </h1>
          <p className={classes.subtitle}>
            Stickers are larger graphics displayed directly on your tour without
            opening a popup. Perfect for logos, labels, or decorative elements.
          </p>
          <div className={classes.grow} />
        </>
      }
    >
      <div className={classes.options}>
        <GenericOptions
          icon="Tag"
          iconSet={[
            "Note",
            "Note Plus",
            "Video",
            "Audio",
            "Link",
            "Photo",
            "Tag",
          ]}
          config={config}
          setConfig={setConfig}
          account={account}
          template={template}
          title={false}
          requireTooltip={false}
        />
        <Label name="Sticker Size" optional>
          <Selector
            options={[
              { label: "Small", value: "small" },
              { label: "Medium", value: "medium" },
              { label: "Large", value: "large" },
              { label: "Extra Large", value: "xlarge" },
              { label: "Custom", value: "custom" },
            ]}
            value={
              config.options.customScale !== null &&
              config.options.customScale !== undefined
                ? "custom"
                : config.options.stickerSize ?? "medium"
            }
            onChange={size => {
              setConfig(
                produce(draft => {
                  if (size === "custom") {
                    draft.options.stickerSize = "custom";
                    if (draft.options.customScale === null || draft.options.customScale === undefined) {
                      draft.options.customScale = "2";
                    }
                  } else {
                    draft.options.stickerSize = size;
                    draft.options.customScale = null;
                    draft._options ??= {};
                    const sizeMap = {
                      small: 1,
                      medium: 2,
                      large: 3,
                      xlarge: 4,
                    };
                    draft._options.scale = sizeMap[size] ?? 2;
                  }
                }),
              );
            }}
            expanded={sizeSelectorOpen}
            toggle={() => setSizeSelectorOpen(!sizeSelectorOpen)}
            noLeftMargin
          />
        </Label>
        {config.options.customScale !== null &&
          config.options.customScale !== undefined && (
            <TextInput
              label="Custom Scale"
              optional
              value={String(config.options.customScale)}
              onChange={value => {
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && numValue > 0) {
                  setConfig(
                    produce(draft => {
                      draft.options.customScale = value;
                      draft._options ??= {};
                      draft._options.scale = numValue;
                    }),
                  );
                } else if (value === "") {
                  setConfig(
                    produce(draft => {
                      draft.options.customScale = "";
                      draft._options ??= {};
                      // Fallback to medium if empty
                      draft._options.scale = 2;
                    }),
                  );
                }
              }}
            />
          )}
        <div className={classes.preview}>
          <div className={classes.previewLabel}>Size Preview</div>
          <div className={classes.previewContainer}>
            <div
              className={classes.referenceHotspot}
              title="Standard hotspot size (45px)"
            >
              <span>1x</span>
            </div>
            <div
              className={classes.stickerPreview}
              style={{
                width: `${Math.min(stickerSize, 200)}px`,
                height: `${Math.min(stickerSize, 200)}px`,
                left: "50%",
                transform: "translateX(-50%)",
              }}
              title={`Sticker size: ${stickerSize}px (${currentScale}x)`}
            >
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt="Sticker preview"
                  className={classes.previewIcon}
                  onError={() => setIconUrl(null)}
                />
              ) : (
                <span className={classes.previewFallback}>
                  {currentScale.toFixed(1)}x
                </span>
              )}
            </div>
          </div>
          <div className={classes.sizeInfo}>
            Sticker: {stickerSize}px ({currentScale}x scale)
            {config.options.stickerSize && config.options.stickerSize !== "custom" && (
              <span> • {config.options.stickerSize.charAt(0).toUpperCase() + config.options.stickerSize.slice(1)}</span>
            )}
          </div>
        </div>
        <RedButton
          onClick={() => onComplete(config)}
        >
          Save
        </RedButton>
      </div>
    </SidebarLightbox>
  );
};

export default EditStickerHotspot;
